/**
 * Impression and click reporting for advertising placements.
 *
 * Events are APPENDED to `ad_events`; nothing here touches the advert
 * document. The counters an advertiser is billed on must not be writable by a
 * browser — anyone who can read this bundle could otherwise set `clicks` to
 * any number they liked, including zero. `rollupAdEvents` aggregates and
 * trims them server-side.
 *
 * ⚠️ HONEST LIMITS. `ad_events` is publicly writable and App Check is
 * deliberately not enforced yet, so nothing here is proof against a
 * determined forger rotating session ids. What it does prevent is ACCIDENTAL
 * inflation, which is the far more likely distortion:
 *
 *   - one impression per advert per session, ever (not per render, and the
 *     hero carousel re-renders on every autoplay tick and every snapshot);
 *   - the rollup counts distinct sessions, so a flood from one session is
 *     worth exactly one.
 *
 * Treat the numbers as directional until App Check is enforced.
 *
 * NEVER THROWS and never blocks. A failed beacon must not cost the traveller
 * a navigation — an advert click that reports nothing is a lost data point; a
 * click that does not open the listing is a lost booking.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const SESSION_KEY = 'vkch:ad-session';
const SEEN_KEY = 'vkch:ad-seen';

/**
 * A per-tab identifier, not a per-person one.
 *
 * sessionStorage on purpose: this exists to deduplicate within a visit, not
 * to follow anyone between them. It is never sent anywhere but our own
 * `ad_events`, and the rollup discards it.
 */
export function sessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    // Private browsing with storage disabled. A per-load id still dedupes
    // within the page, which is where the repeated renders are.
    return 's-ephemeral';
  }
}

function alreadySeen(adId) {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw).includes(adId) : false;
  } catch (e) {
    return false;
  }
}

function markSeen(adId) {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    const seen = raw ? JSON.parse(raw) : [];
    if (!seen.includes(adId)) {
      seen.push(adId);
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    }
  } catch (e) {
    // As above — dedupe degrades, nothing breaks.
  }
}

function send(ad, kind) {
  // House inventory has no advert document to credit. `adFromListing` mints
  // an id of `listing-{id}`, which is not a real placement and must never
  // reach the rollup as one.
  if (!ad?.id || String(ad.id).startsWith('listing-') || ad.id === 'house-fallback') return;

  // Fire and forget. The rules require `at` to equal the server's clock, so
  // it is stamped there rather than here — a client-set timestamp could
  // backdate an event into a window already rolled up and trimmed.
  addDoc(collection(db, 'ad_events'), {
    adId: String(ad.id),
    slot: String(ad.slot || ''),
    kind,
    at: serverTimestamp(),
    sessionId: sessionId(),
  }).catch((e) => {
    console.warn('ad_events append failed (ignored):', e?.code || e);
  });
}

/** Once per advert per session. Safe to call on every render. */
export function recordImpression(ad) {
  if (!ad?.id || alreadySeen(ad.id)) return;
  markSeen(ad.id);
  send(ad, 'IMPRESSION');
}

/** Called from the CTA. Must not delay or cancel the navigation. */
export function recordClick(ad) {
  send(ad, 'CLICK');
}
